import { useEffect, useRef } from 'react';

import { LIST_RECIPIENTS_PAGE_SIZE } from '../constants/tableFilters';
import { useListRecipientPosition } from '../models/mutations';

const HIGHLIGHT_FADE_MS = 2500;

/**
 * Jump-to-row for a search deep link on a server-paginated list tab. A row on the loaded page is
 * scrolled into view; otherwise its position in the unfiltered tab is resolved, the tab's filters
 * are cleared and the page holding the row is opened. The highlight param is cleared once the
 * highlight has faded, or right away when the row is not in the list anymore.
 *
 * @param {Object} options
 * @param {string} options.listId
 * @param {'contacts'|'companies'} options.kind - Tab the highlighted entity lives in
 * @param {string|null} options.highlightId - Entity id from the ?highlight URL param
 * @param {boolean} options.settled - False while the tab's page query is (re)fetching
 * @param {Array<Object>} options.rows - Rows of the loaded page
 * @param {Function} options.getRowId - Maps a row to the entity id used in highlight links
 * @param {Object} options.urlFilters - The tab's useUrlFilters result
 * @param {Function} options.onConsumed - Clears the highlight URL param (stable identity)
 * @returns {void}
 */
export function useRowHighlight({ listId, kind, highlightId, settled, rows, getRowId, urlFilters, onConsumed }) {
	const isOnPage = Boolean(highlightId) && rows.some((row) => getRowId(row) === highlightId);
	const { page, isFiltered, showUnfilteredPage } = urlFilters;
	const { mutateAsync: fetchPosition } = useListRecipientPosition();
	// { id, page } of the jump started for a highlight; page stays null while the lookup runs.
	const jumpRef = useRef(null);
	const scrolledRef = useRef(null);

	useEffect(() => {
		if (!highlightId) {
			jumpRef.current = null;
			return;
		}
		if (!settled) return;
		if (isOnPage) {
			const timer = setTimeout(onConsumed, HIGHLIGHT_FADE_MS);
			return () => clearTimeout(timer);
		}

		const jump = jumpRef.current;
		if (jump?.id === highlightId) {
			const landed = jump.page === page && !isFiltered;
			if (landed) onConsumed();
			return;
		}

		jumpRef.current = { id: highlightId, page: null };
		fetchPosition({ listId, kind, recipientId: highlightId, pageSize: LIST_RECIPIENTS_PAGE_SIZE })
			.then((position) => {
				if (jumpRef.current?.id !== highlightId) return;
				const alreadyShown = position?.page === page && !isFiltered;
				if (!position || alreadyShown) {
					onConsumed();
					return;
				}
				jumpRef.current = { id: highlightId, page: position.page };
				showUnfilteredPage(position.page);
			})
			.catch(() => onConsumed());
	}, [highlightId, settled, isOnPage, page, isFiltered, listId, kind, fetchPosition, showUnfilteredPage, onConsumed]);

	useEffect(() => {
		if (!isOnPage || scrolledRef.current === highlightId) return;
		const el = document.getElementById(`list-row-${highlightId}`);
		if (!el) return;
		scrolledRef.current = highlightId;
		requestAnimationFrame(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }));
	});
}
